// --------------------------
// IMPORTS
// --------------------------
import {
  Row,
  Col,
  Form,
  DatePicker,
  Input,
  message,
  Table,
  Modal,
  Checkbox,
} from "antd";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Select from "react-select";
import Column from "antd/es/table/Column";
import CustomLoader from "../../utils/CustomLoader";
import useCurrentPath, {
  decryptData,
  draggerProps,
  getRateByDate,
} from "../../utils/Constants";
import {
  addOrDraftExpenseThunk,
  getAdminApproverListThunk,
  getBlockedDateThunk,
} from "../../store/thunks/addNewExpenseThunk";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import AddNewButton from "../../components/atoms/Buttons/AddNewButton";
import media from "../../media";
import Dragger from "antd/es/upload/Dragger";

import {
  getCurrencyListThunk,
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getProjectCodeListThunk,
  getUserListThunk,
  getVendorListThunk,
  getDiemListThunk,
  getLocationListThunk,
} from "../../store/thunks/masterDataThunk";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function AddExpense({ isVisible, setIsVisible }) {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPath = useCurrentPath();
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();
  const tableTotalRef = useRef(0);

  // --------------------------
  // REDUX STATE
  // --------------------------

  let isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);

  const isDelegated = useSelector((state) => state.auth.isDelegated);
  const actingUser = useSelector((state) => state.auth.actingUser);

  const activeUser =
    isDelegated && actingUser ? actingUser : storeUserInfo?.user;

  const storeEmpID = activeUser?.empID;
  const storeEmpName = activeUser?.name;
  const storeEmpDepartmentID = activeUser?.departmentID;

  const storeEmpProjectCode = activeUser?.projectCodeID;

  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formAction, setFormAction] = useState("");
  const [currencyRates, setCurrencyRates] = useState({});
  const [currenciesInfo, setCurrenciesInfo] = useState({});
  const [blockedDate, setBlockedDate] = useState(null);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminApprovers, setAdminApprovers] = useState([]);
  const [diems, setDiems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [natureOfExpenseSelectedValue, setNatureOfExpenseSelectedValue] =
    useState();
  const [natureOfExpense, setNatureOfExpense] = useState([
    { label: "Already Paid - Filling", value: "Already Paid - Filling" },
    { label: "Reimbursement", value: "Reimbursement" },
    { label: "Travel Settlement", value: "Travel Settlement" },
    { label: "Vendor Bill", value: "Vendor Bill" },
  ]);
  const [addExpenseValues, setAddExpenseValues] = useState(null);
  const [expenseValues, setExpenseValues] = useState({
    empName: "",
    empID: "",
    departmentID: "",
    expensePeriodFromDate: "",
    expensePeriodToDate: "",
    expenseEmpIDRole: "",
    businessPurpose: "",
    natureOfExpense: "",
    expensesDetails: "",
    subTotal: "",
    totalReimbursement: "",
    location: null,
    numberOfDays: 0,
    perDiem: 0,
    exchangeRate: 0,
    totalForeignAmount: 0,
    totalINRAmount: 0,
    advanceAmount: 0,
    travelSettlementFromDate: "",
    travelSettlementToDate: "",
    travelSettleMediaInfo: [],
  });
  const [rows, setRows] = useState([
    {
      date: "",
      description: "",
      departmentID: "",
      expenseCodeID: "",
      currencyID: "",
      recharge: "No",
      cost: "",
      mediaInfo: [],
    },
  ]);
  const [rechargeOpts, setRechargeOpts] = useState([
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ]);
  const [mediaInfoRows, setMediaInfoRows] = useState([
    {
      files: [],
    },
  ]);
  const [travelMediaItem, setTravelMediaItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanceAmount, setShowAdvanceAmount] = useState(false);

  const storeEmpDepartmentName = isDelegated
    ? departments
        .find((dept) => dept.value === storeEmpDepartmentID)
        ?.label?.split("-")?.[0]
        ?.trim()
    : storeUserInfo?.userDepartment;

  const storeEmpDepartmentCode = isDelegated
    ? departments
        .find((dept) => dept.value === storeEmpDepartmentID)
        ?.label?.split("-")?.[1]
        ?.trim()
    : storeUserInfo?.userDepartmentCode;

  const parseToFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const subTotal = rows.reduce(
    (total, row) => total + parseToFloat(row.cost),
    0,
  );

  //const totalReimbursement = subTotal.toFixed(2);
  const initialTotalReimbursement = 0;
  const [totalReimbursement, setTotalReimbursement] = useState(
    initialTotalReimbursement.toFixed(2),
  );

  // --------------------------
  // API CALLS
  // --------------------------

  const expCal = () => {
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    dispatch(getBlockedDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response?.expCalRes) {
          response.expCalRes?.blockedOn &&
          response.expCalRes?.status === process.env.REACT_APP_BLOCK_STATUS
            ? setBlockedDate(response.expCalRes)
            : setBlockedDate(null);
        }
      });
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  useEffect(() => {
    expCal();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      // Run the function immediately
      if (storeRole !== process.env.REACT_APP_SROLE) {
        expCal();
      }
      const intervalId = setInterval(() => {
        if (storeRole !== process.env.REACT_APP_SROLE) {
          expCal();
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [storeRole, storeToken, isLoggedIn]);

  useEffect(() => {
    if (storeEmpID) {
      const currEmpID = parseInt(storeEmpID);
      const emp = users.find((emp) => emp.empID === currEmpID);

      const expenseEmpIDRole = emp ? emp.roleID?.name : "";

      setExpenseValues({
        ...expenseValues,
        expenseEmpIDRole: expenseEmpIDRole,
      });
      form.setFieldsValue({ empID: storeEmpID });
      form.setFieldsValue({ empName: storeEmpName });
      form.setFieldsValue({
        departmentID: `${storeEmpDepartmentName} - ${storeEmpDepartmentCode}`,
      });
    }
  }, []);

  useEffect(() => {
    const requests = [
      dispatch(getCurrencyListThunk()).unwrap(),
      dispatch(getDepartmentListThunk()).unwrap(),
      dispatch(getVendorListThunk()).unwrap(),
      dispatch(getExpenseCodeListThunk()).unwrap(),
      dispatch(getProjectCodeListThunk()).unwrap(),
      dispatch(getUserListThunk()).unwrap(),
      dispatch(getDiemListThunk()).unwrap(),
      dispatch(getLocationListThunk()).unwrap(),
    ];

    if (storeRole === process.env.REACT_APP_ADROLE) {
      requests.push(
        dispatch(
          getAdminApproverListThunk({
            category: process.env.REACT_APP_EXPENSE_CATEGORY_CAPEX,
          }),
        ).unwrap(),
      );
    }

    Promise.all(requests)
      .then(
        ([
          currencyRes,
          departments,
          vendors,
          expenseCodes,
          projectCodes,
          users,
          diems,
          locations,
          adminApprovers,
        ]) => {
          setAllCurrencies(currencyRes.currencyDropdown || []);
          setCurrencyRates(currencyRes.currencyRates || []);
          setCurrenciesInfo(currencyRes.list || []);
          setDepartments(departments || []);
          setVendors(vendors || []);
          setExpenseCodes(expenseCodes || []);
          setProjectCodes(projectCodes || []);
          setUsers(users || []);
          setDiems(diems || []);
          setLocations(locations || []);
          if (adminApprovers) setAdminApprovers(adminApprovers || []);
        },
      )
      .finally(() => setShowLoader(false));
  }, [currentPath, dispatch, storeRole]);

  // useEffect(() => {
  //   if (expenseValues?.natureOfExpense?.value === "Travel Settlement") {
  //     setTotalReimbursement(
  //       (
  //         tableTotalRef.current + Number(expenseValues.totalINRAmount || 0)
  //       ).toFixed(2),
  //     );
  //   }
  // }, [expenseValues.totalINRAmount]);

  useEffect(() => {
    if (expenseValues?.natureOfExpense?.value === "Travel Settlement") return;

    // Reset Advance section FIRST
    setShowAdvanceAmount(false);

    // Clear rows FIRST
    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        //currencyID: "",
        costInINR: "",
        currencyRate: "",
      })),
    );

    //  Clear form fields
    // rows.forEach((_, index) => {
    //   form.setFieldsValue({
    //     rows: {
    //       [index]: { currencyID: null },
    //     },
    //   });
    // });

    // 3️⃣ Clear Travel Settlement fields (especially location)
    setExpenseValues((prev) => ({
      ...prev,
      travelSettlementFromDate: "",
      travelSettlementToDate: "",
      location: null, //  CRITICAL
      numberOfDays: 0,
      perDiem: 0,
      exchangeRate: 0,
      totalForeignAmount: 0,
      totalINRAmount: 0,
      advanceAmount: 0,
      travelSettleMediaInfo: [],
    }));
    form.setFieldsValue({
      advanceAmount: 0,
      travelSettleMediaInfo: [],
    });

    //setTotalReimbursement(tableTotalRef.current.toFixed(2));
  }, [expenseValues?.natureOfExpense]);

  // --------------------------
  // HANDLERS
  // --------------------------

  const getUserDiem = (user, diems) => {
    if (!user?.diemId || !Array.isArray(diems)) return null;

    return diems.find((d) => d._id === user.diemId) || null;
  };
  const buildPerDiemRates = (user, diems) => {
    const diem = getUserDiem(user, diems);
    if (!diem) return {};

    return {
      INR: Number(diem.inr) || 0,
      AUD: Number(diem.aud) || 0,
      USD: Number(diem.usd) || 0,
      GBP: Number(diem.gbp) || 0,
      CAD: Number(diem.cad) || 0,
      EU: Number(diem.eu) || 0,
    };
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Add Expense API
  const onFinish = (values) => {
    if (errors?.fileSizeExceed === "single") {
      message.error("File size exceeds 5 MB.");
    } else if (errors?.fileSizeExceed === "total") {
      message.error("The total size of all files must not exceed 10 MB.");
    } else if (errors?.fileCountExceed) {
      message.error(
        "You cannot only upload one attachment so upload accordingly.",
      );
    } else if (
      natureOfExpenseSelectedValue !== "Travel Settlement" &&
      totalReimbursement.indexOf("-") !== -1
    ) {
      message.error(
        totalReimbursement +
          " amount is with you. If you have any concern then please contact with Finance Department!!",
      );
    } else {
      setAddExpenseValues(values);
      setIsModalVisible(true);
    }
  };

  const handleOk = () => {
    if (!addExpenseValues) return;
    setIsModalVisible(false);
    setShowLoader(true);
    setErrors({ fileSizeExceed: "", fileCountExceed: false });
    const formRows = form.getFieldValue("rows") || [];
    const payload = {
      route: process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN,
      empName: addExpenseValues.empName,
      empID: parseInt(storeEmpID),
      departmentID: storeEmpDepartmentID,
      expensePeriodFromDate: expenseValues?.expensePeriodFromDate,
      expensePeriodToDate: expenseValues?.expensePeriodToDate,
      businessPurpose: addExpenseValues?.businessPurpose?.label,
      natureOfExpense: addExpenseValues?.natureOfExpense?.value,
      projectCodeID: storeEmpProjectCode
        ? storeEmpProjectCode
        : addExpenseValues?.projectCodeID?.value,
      historySource: "portal",
      historyAction: formAction === "draft" ? "save as draft" : "create",
      historyUserID: storeLIUId,
      status:
        formAction === "draft"
          ? process.env.REACT_APP_DRAFT_STATUS.toUpperCase()
          : process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
      isDrafted: process.env.REACT_APP_DRAFT_STATUS.toUpperCase(),
    };

    const expensesDetails = formRows.map((r, i) => ({
      ...rows[i], // keep mediaInfo, costInINR etc
      date: r?.date,
      description: r?.description,
      departmentID: r?.departmentID?.value || r?.departmentID,
      expenseCodeID: r?.expenseCodeID?.value || r?.expenseCodeID,
      currencyID: r?.currencyID?.value || r?.currencyID, //  GUARANTEED
      recharge: r?.recharge, //r?.recharge?.value ||
      cost: r?.cost,
    }));

    payload.expensesDetails = expensesDetails;

    if (adminApprovers.length > 0) {
      payload.adminSelectedApprover = expenseValues?.approvers?.value;
    }

    if (addExpenseValues?.natureOfExpense?.value === "Travel Settlement") {
      payload.travelSettlement = {
        fromDate: expenseValues.travelSettlementFromDate,
        toDate: expenseValues.travelSettlementToDate,
        location: expenseValues.location?.value,
        numberOfDays: expenseValues.numberOfDays,
        perDiem: expenseValues.perDiem,
        exchangeRate: expenseValues.exchangeRate,
        totalForeignAmount: expenseValues.totalForeignAmount,
        totalINRAmount: expenseValues.totalINRAmount,
        advanceAmount: expenseValues.advanceAmount || null,
        travelSettleMediaInfo: expenseValues.travelSettleMediaInfo || [],
      };
    }

    dispatch(addOrDraftExpenseThunk(payload))
      .then(() => {
        message.success(
          formAction === process.env.REACT_APP_DRAFT_STATUS.toLowerCase()
            ? "Expense drafted successfully."
            : "Expense added successfully.",
        );
        form.resetFields();
        form.setFieldsValue({
          empID: storeEmpID,
          empName: storeEmpName,
          travelSettleMediaInfo: [],
        });
        setExpenseValues({
          ...expenseValues,
          advanceAmount: 0,
          travelSettleMediaInfo: [],
        });
        setRows([
          {
            date: "",
            description: "",
            departmentID: "",
            expenseCodeID: "",
            currencyID: "",
            recharge: "No",
            //currencyRateDateTime: "",
            cost: "",
            mediaInfo: [],
          },
        ]); // Reset rows to initial state
        setShowLoader(false);
        navigate(`/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}`);
      })
      .catch((error) => {
        setShowLoader(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setShowLoader(false);
    message.error(
      "Please review the form. Some required fields are missing or invalid.",
    );
  };

  const handleAddRow1 = () => {
    const newIndex = rows.length + 1;
    setRows([
      ...rows,
      {
        title: `Expense Detail #${newIndex}`,
        date: "",
        description: "",
        departmentID: "",
        expenseCodeID: "",
        currencyID: "",
        cost: "",
        recharge: "No",
        mediaInfo: [],
      },
    ]);
  };

  const handleAddRow2 = () => {
    const newRow = {
      id: Date.now(),

      date: null,
      description: "",
      departmentID: null,
      expenseCodeID: null,
      currencyID: null,

      recharge: "No", //  DEFAULT

      cost: "",
      media: [],
    };

    // Update rows state
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);

    //  Update Form state
    const formRows = form.getFieldValue("rows") || [];

    form.setFieldsValue({
      rows: [
        ...formRows,
        {
          ...newRow,
          recharge: "No", //  IMPORTANT
        },
      ],
    });
  };

  const handleAddRow = () => {
    // 1️ Get COMPLETE latest form values
    const allFormValues = form.getFieldsValue(true);
    const currentRows = allFormValues?.rows || [];

    // 2️ Create new row
    const newRow = {
      id: Date.now(),
      date: null,
      description: "",
      departmentID: null,
      expenseCodeID: null,
      currencyID: null,
      recharge: "No", // DEFAULT
      cost: "",
      media: [],
    };

    // 3️ Merge safely
    const updatedRows = [...currentRows, newRow];

    // 4️ Update React state
    setRows(updatedRows);

    // 5️ Update Form state
    form.setFieldsValue({
      rows: updatedRows,
    });
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];

    if (
      field === "departmentID" ||
      field === "expenseCodeID" ||
      field === "currencyID" ||
      field === "recharge"
    ) {
      updatedRows[index][field] = value?.value;
    } else {
      updatedRows[index][field] = value;
    }

    const currentRow = updatedRows[index];
    const { date, currencyID, cost } = currentRow;

    const costValue = parseFloat(cost || 0);

    // Only process if both date and currency are selected
    if (date && currencyID) {
      const matched = getRateByDate(currencyRates, currencyID, date);

      const rate = matched?.rate || 1;
      const rateDateTime = matched?.effectiveDateTime || null;

      updatedRows[index].currencyRate = rate;
      //updatedRows[index].currencyRateDateTime = rateDateTime;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    } else {
      // If not all values selected, just default costInINR = cost
      const rate = currencyRates[currencyID]?.[0]?.rate || 1;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    }

    // Recalculate total reimbursement
    const total = updatedRows.reduce((sum, r) => {
      const val = parseFloat(r.costInINR || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    setRows(updatedRows);
    tableTotalRef.current = total;

    if (expenseValues?.natureOfExpense?.value === "Travel Settlement") {
      // setTotalReimbursement(
      //   (total + Number(expenseValues.totalINRAmount || 0)).toFixed(2),
      // );
    } else {
      //setTotalReimbursement(total.toFixed(2));
    }
  };

  const handleRemoveRow = (indexToRemove) => {
    const updatedRows =
      rows && rows.filter((row, index) => index !== indexToRemove);
    setRows(updatedRows);
    // Convert date fields back to moment objects if they exist
    const updatedFields =
      updatedRows &&
      updatedRows.map((row) => ({
        ...row,
        date: row.date ? moment(row.date) : null,
      }));

    form.setFieldsValue({ rows: updatedFields });
  };

  const handleFileChange = (index, fileOrEvent, onSuccess) => {
    // Handle both Dragger (file object) and Browse File (event)
    const files = fileOrEvent?.target?.files || [fileOrEvent]; // auto-detect source

    if (files.length > 1) {
      setErrors({ fileCountExceed: true });
      message.error("Only one file can be uploaded.");
      return;
    }

    setErrors({ fileCountExceed: false });

    const maxSingleFileSize = 4 * 1024 * 1024; // 4MB
    const file = files[0];
    const updatedRows = [...rows];

    // File size check
    if (file.size > maxSingleFileSize) {
      setErrors({ fileSizeExceed: "single" });
      message.error(`File "${file.name}" exceeds 4MB.`);
      return;
    }

    // File type check
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed!");
      return;
    }

    const reader = new FileReader();
    const fileSources = [];

    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    reader.onload = (e) => {
      let fileData;
      if (file.type.startsWith("image/")) {
        fileData = e.target.result;
      } else {
        const base64Data = arrayBufferToBase64(e.target.result);
        fileData = `data:${file.type};base64,${base64Data}`;
      }

      fileSources.push({
        src: fileData,
        name: file.name,
        type: file.type,
      });

      updateRowFiles(index, fileSources, updatedRows);
      onSuccess("ok");
    };

    // Read file accordingly
    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleTravelMediaChange = (file, onSuccess) => {
    if (!file) return;

    const maxSingleFileSize = 4 * 1024 * 1024;
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];

    if (file.size > maxSingleFileSize) {
      message.error("File exceeds 4MB.");
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const media = [
        {
          src: e.target.result,
          name: file.name,
          type: file.type,
        },
      ];

      setExpenseValues((prev) => ({
        ...prev,
        travelSettleMediaInfo: media,
      }));

      form.setFieldsValue({
        travelSettleMediaInfo: media,
      });

      setTravelMediaItem("freshUpload");
      onSuccess("ok");
    };

    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const updateRowFiles = (index, fileSources, updatedRows) => {
    if (!updatedRows[index].mediaInfo) {
      updatedRows[index].mediaInfo = [];
    }
    updatedRows[index].mediaInfo = fileSources;
    setRows(updatedRows);
  };

  const validationRules = {
    date: [{ required: true, message: "Please select a date" }],
    description: [{ required: true, message: "Please enter a description" }],
    departmentID: [{ required: true, message: "Please select a department" }],
    expenseCodeID: [
      { required: true, message: "Please select an expense code" },
    ],
    currencyID: [{ required: true, message: "Please enter a currency" }],
    recharge: [{ required: true, message: "Please select recharge option" }],
    cost: [
      { required: true, message: "Please enter a cost" },
      {
        pattern: /^[0-9]*\.?[0-9]+$/,
        message: "Only numbers and decimals are allowed",
      },
    ],
    upload: [{ required: true, message: "Please select a file" }],
  };

  const validateTravelMedia = () => {
    if (!showAdvanceAmount) return Promise.resolve();

    return expenseValues.travelSettleMediaInfo?.length
      ? Promise.resolve()
      : Promise.reject(new Error("Please upload advance amount document"));
  };

  const handleButtonClick = (index) => {
    document.getElementById(`fileInput${index}`).click();
  };

  // Disable dates after the current date
  const disableFutureDates = (current) => {
    const startDate = moment(current).subtract(1, "month").startOf("day");

    // Calculate the end date for the next financial year
    const currentYear = moment().year();
    const nextFinancialYearEnd = moment()
      .month(2)
      .date(31)
      .year(
        currentYear + (moment().isAfter(moment().month(2).date(31)) ? 1 : 0),
      )
      .endOf("day");

    // Disable dates outside the range two months ago from today to next financial year's 31st March
    return (
      current &&
      (current.isBefore(startDate) || current.isAfter(moment().endOf("day")))
    );
  };

  const validateFilePresence = (_, value) => {
    return rows.some((row) => row.mediaInfo && row.mediaInfo.length > 0)
      ? Promise.resolve()
      : Promise.reject(new Error("Please upload a file!"));
  };

  function getMinDate(dateStr) {
    const date = new Date(dateStr); // e.g., "2025-05-22"
    const year = date.getFullYear();
    const month = date.getMonth(); // Current month (0-indexed)

    // Set to 0th day of current month to get last day of previous month
    return new Date(year, month, 0); // e.g., 30 April 2025
  }

  // Preprocess rows to add a stable 'id' if missing
  const rowsWithIds = rows && rows.map((row, idx) => ({ ...row, id: idx }));
  const formattedDate = new Date()
    .toLocaleString("sv-SE", { hour12: false })
    .replace("T", " ")
    .slice(0, 16);

  //  Travel Perdiem calculation start here
  const currencyOptions = locations && locations;
  const userDiemRates = buildPerDiemRates(storeUserInfo?.user, diems);
  const PER_DIEM_RATES = useMemo(() => {
    return buildPerDiemRates(storeUserInfo?.user, diems);
  }, [storeUserInfo?.user?.diemId, diems]);

  const getNumberOfDays = (from, to) => {
    if (!from || !to) return 0;

    const start = moment(from, "YYYY-MM-DD");
    const end = moment(to, "YYYY-MM-DD");

    return end.diff(start, "days") + 1;
  };
  const getExchangeRateToINR = (
    currencyCode,
    currenciesInfo,
    effectiveDate,
  ) => {
    if (!currencyCode) return 1;

    const currency = currenciesInfo.find(
      (c) => String(c.code) === String(currencyCode),
    );

    if (!currency?.currencyRateHistory?.length) return 1;

    const sorted = [...currency.currencyRateHistory].sort(
      (a, b) => new Date(b.effectiveDateTime) - new Date(a.effectiveDateTime),
    );

    const applicable =
      sorted.find(
        (r) => new Date(r.effectiveDateTime) <= new Date(effectiveDate),
      ) || sorted[0];

    return Number(applicable?.rate) || 1;
  };

  useEffect(() => {
    const { travelSettlementFromDate, travelSettlementToDate, location } =
      expenseValues;

    if (
      travelSettlementFromDate &&
      travelSettlementToDate &&
      expenseValues.location?.label?.split("-")?.[1]?.trim()
    ) {
      const days =
        moment(travelSettlementToDate).diff(
          moment(travelSettlementFromDate),
          "days",
        ) + 1;

      const currency = expenseValues.location?.label?.split("-")?.[1]?.trim();

      const perDiem = PER_DIEM_RATES?.[currency] || 0;

      const exchangeRate = getExchangeRateToINR(
        currency,
        currenciesInfo,
        travelSettlementFromDate,
      );

      const totalForeignAmount = days * perDiem;
      const totalINRAmount = totalForeignAmount * exchangeRate;

      setExpenseValues((prev) => ({
        ...prev,
        numberOfDays: days,
        perDiem,
        exchangeRate,
        totalForeignAmount: Number(totalForeignAmount || 0).toFixed(2),
        totalINRAmount: Number(totalINRAmount || 0).toFixed(2),
      }));
    }
  }, [
    expenseValues.travelSettlementFromDate,
    expenseValues.travelSettlementToDate,
    expenseValues.location,
  ]);

  //  Travel Perdiem calculation close here

  const getCurrencyCodeFromLocation = (location) => {
    if (!location?.label) return null;
    return location.label.split("-")[1]?.trim(); // USD, AUD
  };

  const getCurrencyOptionByCode = (code) => {
    if (!code) return null;
    return allCurrencies.find((c) => c.label === code);
  };

  useEffect(() => {
    if (expenseValues?.natureOfExpense?.value !== "Travel Settlement") return;

    const currencyCode = getCurrencyCodeFromLocation(expenseValues.location);
    if (!currencyCode) return;

    const currencyOption = getCurrencyOptionByCode(currencyCode);
    if (!currencyOption) return;

    setRows((prevRows) => {
      // Get current form rows
      const formRows = form.getFieldValue("rows") || [];
      const updatedRows = prevRows.map((row, index) => {
        // keep if already set
        if (row.currencyID) return row;

        const updated = {
          ...row,
          ...formRows[index],
          currencyID: currencyOption, //  STORE FULL OBJECT
        };

        if (row.date && row.cost) {
          const matched = getRateByDate(
            currencyRates,
            currencyOption.value,
            row.date,
          );
          const rate = matched?.rate || 1;
          updated.costInINR = (Number(row.cost) * rate).toFixed(2);
          updated.currencyRate = rate;
        }

        return updated;
      });

      form.setFieldsValue({
        rows: updatedRows,
      });

      return updatedRows;
    });
  }, [
    expenseValues.location,
    expenseValues.natureOfExpense,
    currencyRates,
    rows.length,
  ]);

  const isTravelSettlement =
    expenseValues?.natureOfExpense?.value === "Travel Settlement";

  const isTravelSettlementWithLocation =
    isTravelSettlement && !!expenseValues?.location;
  // sticky code start here
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const STICKY_OFFSET = 54;

  const handleScroll = () => {
    if (!wrapperRef.current || !stickyRef.current) return;

    const wrapperTop = wrapperRef.current.getBoundingClientRect().top;
    const stickyTop = stickyRef.current.getBoundingClientRect().top;

    // Trigger when sticky reaches 55px from top
    setIsSticky(stickyTop <= wrapperTop + STICKY_OFFSET);
  };

  //For showing default Project Code
  useEffect(() => {
    if (storeEmpProjectCode && projectCodes.length) {
      const selectedProject = projectCodes.find(
        (opt) => opt.value === storeEmpProjectCode,
      );

      if (selectedProject) {
        form.setFieldsValue({
          projectCodeID: selectedProject,
        });

        setExpenseValues((prev) => ({
          ...prev,
          projectCodeID: selectedProject,
        }));
      }
    }
  }, [storeEmpProjectCode, projectCodes]);

  useEffect(() => {
    const rowsTotal = rows.reduce((sum, r) => {
      try {
        const cost = Number(r.cost || 0);
        if (!cost) return sum;

        const currencyId =
          r.currencyID?._id || r.currencyID?.value || r.currencyID;

        const date =
          typeof r.date === "string" ? r.date : r.date?.format?.("YYYY-MM-DD");

        // If currency/date missing → treat as INR
        if (!currencyId || !date) {
          return sum + cost; //  DO NOT count yet
        }

        const matched = getRateByDate(currencyRates, currencyId, date);
        const rate = matched?.rate || 1;

        return sum + cost * rate;
      } catch (e) {
        console.error("❌ Row calculation failed:", e, r);
        return sum;
      }
    }, 0);

    const isTravelSettlement =
      expenseValues?.natureOfExpense?.value === "Travel Settlement";

    const travelINR = isTravelSettlement
      ? Number(expenseValues?.totalINRAmount || 0)
      : 0;

    const advance = isTravelSettlement
      ? Number(expenseValues?.advanceAmount || 0)
      : 0;

    const finalTotal = (rowsTotal + travelINR - advance).toFixed(2);
    setTotalReimbursement(finalTotal);
  }, [
    rows,
    expenseValues?.natureOfExpense,
    expenseValues?.totalINRAmount,
    expenseValues?.advanceAmount,
    currencyRates,
  ]);

  // sticky code end here
  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      {isVisible && (
        <div className="addExpensePopupForm">
          <div
            className="addExpensePopupFormWraper"
            ref={wrapperRef}
            onScroll={handleScroll}
          >
            {showLoader ? (
              <CustomLoader showLoader={showLoader} />
            ) : (
              <>
                <div className="expenseDetailTitle contentBottomPadding">
                  <h3 className="text-dark font-size-lg">Add New Expense</h3>
                  <button
                    onClick={() => setIsVisible(false)}
                    className="expenseDetailTitleClose"
                  >
                    <img src={media.popupCross.svg} alt="close" />
                  </button>
                </div>
                <div className="customExpenseLabelForm">
                  <Form
                    layout="vertical"
                    autoComplete="off"
                    className="form-validation-start"
                    onFinish={onFinish}
                    form={form}
                    onFinishFailed={onFinishFailed}
                    initialValues={{
                      rows: [{ mediaInfo: [], recharge: "No" }],
                    }}
                  >
                    <div style={{ padding: "0 9px" }}>
                      <Row className="addMainWrap" gutter={[18, 24]}>
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="empID"
                            name="empID"
                            rules={[
                              {
                                pattern: /^[0-9]*\.?[0-9]+$/,
                                message:
                                  "Alpbhabets and special symbols not allowing in Employee ID",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="empID"
                                className="block mb-1 font-medium"
                              >
                                Employee ID
                              </label>
                              <Input
                                className="form-control h-45px rounded-md"
                                //value={expenseValues?.empID}
                                value={storeEmpID}
                                disabled={storeEmpID !== 1 ? "disabled" : ""}
                              />
                            </div>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="empName"
                            name="empName"
                            rules={[
                              {
                                pattern: /^[^\d]*$/,
                                message:
                                  "Numbers are not allowed in the employee name",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="empName"
                                className="block mb-1 font-medium"
                              >
                                Employee Name
                              </label>
                              <Input
                                className="form-control h-45px rounded-md"
                                value={storeEmpName}
                                disabled
                              />
                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="departmentID"
                            name="departmentID"
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="departmentID"
                                className="block mb-1 font-medium"
                              >
                                Department
                              </label>
                              <Input
                                className="form-control h-45px rounded-md"
                                value={`${storeEmpDepartmentName} - ${storeEmpDepartmentCode}`}
                                disabled="disabled"
                                placeholder="Select Department"
                              />
                            </div>

                            {/* <Select
                          allowClear
                          className="rounded-md h-45px"
                          placeholder="Select Department"
                          options={departments}
                          value={expenseValues?.departmentID}
                          onChange={(selectedOption) =>
                            setExpenseValues({
                              ...expenseValues,
                              departmentID: selectedOption,
                            })
                          }
                        /> */}
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="expensePeriodFromDate"
                            name="expensePeriodFromDate"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please enter Expense Period: (From - To)",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="expensePeriodFromDate"
                                className="block mb-1 font-medium"
                              >
                                Expense Period: (From - To)
                                <span className="mandatory-symbol">*</span>
                              </label>

                              <RangePicker
                                className="form-control h-45px rounded-md d-inline-flex"
                                format="DD-MM-YYYY"
                                //disabledDate={disableFutureDates}
                                disabledDate={(current) => {
                                  const minDate = new Date(
                                    blockedDate?.blockedOn,
                                  );
                                  return (
                                    current &&
                                    current.toDate() <= getMinDate(minDate)
                                  );
                                }}
                                onChange={(date, dates) => {
                                  if (!dates) {
                                    // cleared
                                    form.setFieldsValue({
                                      expensePeriodFromDate: null,
                                    });

                                    setExpenseValues((prev) => ({
                                      ...prev,
                                      expensePeriodFromDate: null,
                                      expensePeriodToDate: null,
                                    }));
                                    return;
                                  }

                                  const formattedDates =
                                    dates &&
                                    dates.map((date) =>
                                      moment(date, "DD-MM-YYYY").format(
                                        "YYYY-MM-DD",
                                      ),
                                    );
                                  if (
                                    formattedDates &&
                                    formattedDates[0] &&
                                    formattedDates[1]
                                  ) {
                                    form.setFieldsValue({
                                      expensePeriodFromDate: formattedDates,
                                    });
                                    setExpenseValues((prev) => ({
                                      ...prev,
                                      expensePeriodFromDate: formattedDates[0],
                                      expensePeriodToDate: formattedDates[1],
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="projectCodeID"
                            name="projectCodeID"
                            rules={[
                              {
                                required: true,
                                message: "Please select project code",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="projectCodeID"
                                className="block mb-1 font-medium"
                              >
                                Project Code
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Select
                                className="h-45px lh-1Pt5 rounded-md"
                                onChange={(value) => {
                                  form.setFieldsValue({ projectCodeID: value });
                                  setExpenseValues((prev) => ({
                                    ...prev,
                                    projectCodeID: value,
                                  }));
                                }}
                                value={form.getFieldValue("projectCodeID")}
                                // defaultValue={projectCodes.find(
                                //   (opt) => opt.value === storeEmpProjectCode
                                // )}
                                options={projectCodes}
                              />
                            </div>
                          </Form.Item>
                        </Col>

                        {storeRole === process.env.REACT_APP_ADROLE && (
                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              key="approvers"
                              name="approvers"
                              rules={[
                                {
                                  required: true,
                                  message: "Please select approver",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="approvers"
                                  className="block mb-1 font-medium"
                                >
                                  Approvers
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Select
                                  className="h-45px lh-1Pt5 rounded-md"
                                  onChange={(value) => {
                                    form.setFieldsValue({ approvers: value });
                                    setExpenseValues((prev) => ({
                                      ...prev,
                                      approvers: value,
                                    }));
                                  }}
                                  options={adminApprovers}
                                  value={form.getFieldValue("approvers")}
                                />
                              </div>
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="natureOfExpense"
                            name="natureOfExpense"
                            rules={[
                              {
                                required: true,
                                message: "Nature of Expense is Mandatory",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="natureOfExpense"
                                className="block mb-1 font-medium"
                              >
                                Nature of Expense
                                <span className="mandatory-symbol">*</span>
                              </label>
                              <Select
                                className="h-45px lh-1Pt5 rounded-md"
                                allowClear
                                options={natureOfExpense}
                                value={form.getFieldValue("natureOfExpense")}
                                onChange={(value) => {
                                  setNatureOfExpenseSelectedValue(value?.value);
                                  form.setFieldsValue({
                                    natureOfExpense: value,
                                  });
                                  setExpenseValues((prev) => ({
                                    ...prev,
                                    natureOfExpense: value,
                                  }));
                                }}
                              />
                            </div>
                          </Form.Item>
                        </Col>

                        {natureOfExpenseSelectedValue !==
                          "Travel Settlement" && (
                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              key="businessPurpose"
                              name="businessPurpose"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter vendor name",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="businessPurpose"
                                  className="block mb-1 font-medium"
                                >
                                  Vendor Name
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Select
                                  className="h-45px lh-1Pt5 rounded-md"
                                  allowClear
                                  options={vendors}
                                  value={form.getFieldValue("businessPurpose")}
                                  onChange={(value) => {
                                    form.setFieldsValue({
                                      businessPurpose: value,
                                    });
                                    setExpenseValues((prev) => ({
                                      ...prev,
                                      businessPurpose: value,
                                    }));
                                  }}
                                />
                              </div>
                            </Form.Item>
                          </Col>
                        )}

                        {/* <Col sm={24}>
                        <Form.Item
                          className="m-0"
                          key="businessPurpose"
                          name="businessPurpose"
                          rules={[
                            {
                              required: true,
                              message: "Please enter vendor name",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="businessPurpose"
                              className="block mb-1 font-medium"
                            >
                              Vendor Name<span className="mandatory-symbol">*</span>
                            </label>
                            <TextArea
                              placeholder="Enter the vendor name"
                              className="form-control rounded-md"
                              autoSize={{
                                minRows: 2,
                                maxRows: 4,
                              }}
                              onChange={(e) => {
                                setExpenseValues({
                                  ...expenseValues,
                                  businessPurpose: e.target.value,
                                });
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col> */}
                      </Row>
                    </div>

                    {/* Travel Expense Section start here */}
                    {expenseValues?.natureOfExpense?.value ===
                      "Travel Settlement" && (
                      <div className="travelSettlement">
                        <h3>Travel Settlement</h3>
                        <Row className="travelSettlementRow" gutter={[18, 24]}>
                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              key="travelSettlementPeriod"
                              name="travelSettlementPeriod"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Please enter Travel Settlement Period: (From - To)",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="travelSettlementPeriod"
                                  className="block mb-1 font-medium"
                                >
                                  Travel Settlement Period: (From - To)
                                  <span className="mandatory-symbol">*</span>
                                </label>

                                <RangePicker
                                  className="form-control h-45px rounded-md d-inline-flex"
                                  format="DD-MM-YYYY"
                                  disabledDate={(current) => {
                                    const minDate = new Date(
                                      blockedDate?.blockedOn,
                                    );
                                    return (
                                      current &&
                                      current.toDate() <= getMinDate(minDate)
                                    );
                                  }}
                                  onChange={(date, dates) => {
                                    const formattedDates =
                                      dates &&
                                      dates.map((date) =>
                                        moment(date, "DD-MM-YYYY").format(
                                          "YYYY-MM-DD",
                                        ),
                                      );

                                    if (
                                      formattedDates?.[0] &&
                                      formattedDates?.[1]
                                    ) {
                                      form.setFieldsValue({
                                        travelSettlementPeriod: formattedDates,
                                      });

                                      setExpenseValues((prev) => ({
                                        ...prev,
                                        travelSettlementFromDate:
                                          formattedDates[0],
                                        travelSettlementToDate:
                                          formattedDates[1],
                                      }));
                                    }
                                  }}
                                />
                              </div>
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={12} xl={12}>
                            <Form.Item
                              className="m-0"
                              key="location"
                              name="location"
                              rules={[
                                {
                                  required: true,
                                  message: "Please select location",
                                },
                              ]}
                            >
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="location"
                                  className="block mb-1 font-medium"
                                >
                                  Location
                                  <span className="mandatory-symbol">*</span>
                                </label>
                                <Select
                                  className="h-45px lh-1Pt5 rounded-md"
                                  options={currencyOptions}
                                  value={expenseValues.location}
                                  onChange={(value) => {
                                    setExpenseValues((prev) => ({
                                      ...prev,
                                      location: value,
                                    }));
                                    form.setFieldsValue({ location: value });
                                  }}
                                />
                              </div>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row
                          gutter={[18, 24]}
                          className="mt-8 travelFieldsReadOnly"
                        >
                          <Col xs={24} md={6}>
                            <Form.Item>
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="numberOfDays"
                                  className="block mb-1 font-medium"
                                >
                                  No. of Days
                                </label>
                                <Input
                                  className="form-control h-45px rounded-md"
                                  value={expenseValues.numberOfDays}
                                  disabled
                                />
                              </div>
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={6}>
                            <Form.Item>
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="perDiem"
                                  className="block mb-1 font-medium"
                                >
                                  Per Diem
                                </label>
                                <Input
                                  className="form-control h-45px rounded-md"
                                  value={`${
                                    expenseValues.location?.label
                                      ?.split("-")?.[1]
                                      ?.trim() || ""
                                  } ${expenseValues.perDiem || 0}`}
                                  disabled
                                />
                              </div>
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={6}>
                            <Form.Item>
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="totalForeignAmount"
                                  className="block mb-1 font-medium"
                                >
                                  Total
                                </label>
                                <Input
                                  className="form-control h-45px rounded-md"
                                  value={`${
                                    expenseValues.location?.label
                                      ?.split("-")?.[1]
                                      ?.trim() || ""
                                  } ${expenseValues.totalForeignAmount || 0}`}
                                  disabled
                                />
                              </div>
                            </Form.Item>
                          </Col>

                          {/* <Col xs={24} md={6}>
                              <Form.Item label="Exchange Rate">
                                <Input
                                  value={`${expenseValues.exchangeRate || 0}`}
                                  disabled
                                />
                              </Form.Item>
                            </Col>         */}
                          <Col xs={24} md={6}>
                            <Form.Item>
                              <div className="form-row-wrap">
                                <label
                                  htmlFor="totalINR"
                                  className="block mb-1 font-medium"
                                >
                                  Total (INR)
                                </label>
                                <Input
                                  className="form-control h-45px rounded-md"
                                  value={`INR ${
                                    expenseValues.totalINRAmount || 0
                                  }`}
                                  disabled
                                />
                              </div>
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    )}
                    {expenseValues?.natureOfExpense?.value ===
                      "Travel Settlement" &&
                      expenseValues?.location &&
                      getCurrencyCodeFromLocation(expenseValues?.location) !==
                        "INR" && (
                        <>
                          <div className="travelSettlement">
                            <h3>Advance Amount</h3>
                            <Checkbox
                              checked={showAdvanceAmount}
                              onChange={(e) => {
                                setShowAdvanceAmount(e.target.checked);

                                //  Reset advance when unchecked
                                if (!e.target.checked) {
                                  setExpenseValues((prev) => ({
                                    ...prev,
                                    advanceAmount: 0,
                                    travelSettleMediaInfo: [],
                                  }));

                                  form.setFieldsValue({
                                    advanceAmount: 0,
                                    travelSettleMediaInfo: [],
                                  });
                                }
                              }}
                            >
                              Any Advance Amount?
                            </Checkbox>
                            {showAdvanceAmount && (
                              <Row
                                gutter={[18, 24]}
                                className="mt-8 travelFieldsReadOnly"
                              >
                                <Col xs={24} md={24}>
                                  <Form.Item
                                    name="advanceAmount"
                                    dependencies={["showAdvanceAmount"]}
                                    rules={[
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          if (
                                            showAdvanceAmount &&
                                            (!value || Number(value) <= 0)
                                          ) {
                                            return Promise.reject(
                                              new Error(
                                                "Please enter advance amount",
                                              ),
                                            );
                                          }
                                          return Promise.resolve();
                                        },
                                      }),
                                    ]}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="advanceAmount"
                                        className="block mb-1 font-medium"
                                      >
                                        Advance Amount (INR)
                                      </label>
                                      <Input
                                        className="form-control h-45px rounded-md"
                                        type="number"
                                        placeholder="Please enter the advance amount (INR)"
                                        onChange={(e) => {
                                          setExpenseValues((prev) => ({
                                            ...prev,
                                            advanceAmount: e.target.value,
                                          }));
                                          form.setFieldsValue({
                                            advanceAmount: e.target.value,
                                          });
                                        }}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Form.Item
                                    name="travelSettleMediaInfo"
                                    rules={[{ validator: validateTravelMedia }]}
                                  >
                                    <div className="form-row-wrap">
                                      <Dragger
                                        className="uploadDrag"
                                        multiple={false}
                                        customRequest={({ file, onSuccess }) =>
                                          handleTravelMediaChange(
                                            file,
                                            onSuccess,
                                          )
                                        }
                                      >
                                        <p className="ant-upload-drag-icon">
                                          <img src={media.upload.svg} />
                                        </p>
                                        <p className="ant-upload-text">
                                          Drag your file(s) to start uploading
                                        </p>
                                        <p className="ant-upload-hint">OR</p>
                                        <span className="uploadBtn">
                                          <img
                                            src={media.uploadIcon.svg}
                                            alt="Upload info"
                                            title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                          />{" "}
                                          Browse file
                                        </span>
                                        <p className="ant-upload-text">
                                          Only one file allowed (.jpg, .png,
                                          .pdf, max 4MB)
                                        </p>
                                      </Dragger>
                                    </div>
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </div>
                        </>
                      )}
                    {/* Travel Expense Section close here */}

                    <div className="main-repeater expenseAddRepeater">
                      <div
                        ref={stickyRef}
                        className={`addExpenseSubGroup sticky-bar ${
                          isSticky ? "is-sticky" : ""
                        }`}
                      >
                        <h3>Add Expense Details</h3>
                        <AddNewButton
                          addNewButtonClassName="addMoreBtn"
                          addNewButtonName="Add More"
                          btnType="button"
                          onClick={handleAddRow}
                          setShowLoader={setShowLoader}
                        />
                      </div>
                      <Table
                        className="table"
                        dataSource={rowsWithIds}
                        rowKey="id"
                        pagination={false}
                        showHeader={false}
                      >
                        <Column
                          title="Expense Details"
                          key="expenseDetails"
                          render={(text, record, index) => (
                            <div className="flex flex-col gap-2">
                              <h3 className="text-dark font-size-lg expenseRepeaterDetailHeading">
                                {record.title || `Expense Detail #${index + 1}`}
                              </h3>
                              {/* Stack form items vertically */}
                              <Row gutter={[18, 12]}>
                                {/* Date */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    name={["rows", index, "date"]}
                                    rules={validationRules.date}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="date"
                                        className="block mb-1 font-medium"
                                      >
                                        Date
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <DatePicker
                                        disabledDate={(current) => {
                                          const minDate = new Date(
                                            blockedDate?.blockedOn,
                                          );
                                          return (
                                            current &&
                                            current.toDate() <=
                                              getMinDate(minDate)
                                          );
                                        }}
                                        format="DD-MM-YYYY"
                                        // value={
                                        //   record?.date ? moment(record?.date) : null
                                        // }
                                        className="form-control h-45px rounded-md d-inline-flex"
                                        placeholder="Select Date"
                                        onChange={(date) => {
                                          const formattedDate = date
                                            ? date.format("YYYY-MM-DD")
                                            : null;
                                          handleChange(
                                            index,
                                            "date",
                                            formattedDate,
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "date"],
                                            formattedDate,
                                          );
                                        }}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Description */}
                                  <Form.Item
                                    name={["rows", index, "description"]}
                                    rules={validationRules.description}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="description"
                                        className="block mb-1 font-medium"
                                      >
                                        Description
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Input
                                        placeholder="Description"
                                        value={record.description}
                                        className="form-control h-45px rounded-md"
                                        onChange={(e) =>
                                          handleChange(
                                            index,
                                            "description",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Department */}
                                  <Form.Item
                                    name={["rows", index, "departmentID"]}
                                    rules={validationRules.departmentID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`departmentID-${index}`}
                                        className="block mb-1 font-medium"
                                      >
                                        Department
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Select
                                        id={`departmentID-${index}`}
                                        //value={record.departmentID}
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "departmentID",
                                            value,
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "departmentID"],
                                            value,
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                        options={departments}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Expense Code */}
                                  <Form.Item
                                    name={["rows", index, "expenseCodeID"]}
                                    rules={validationRules.expenseCodeID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`expenseCode-${index}`}
                                        className="block mb-1 font-medium"
                                      >
                                        Expense Code
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Select
                                        id={`expenseCode-${index}`}
                                        //value={record.expenseCodeID}
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "expenseCodeID",
                                            value,
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "expenseCodeID"],
                                            value,
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                        options={expenseCodes}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Currency */}
                                  <Form.Item
                                    name={["rows", index, "currencyID"]}
                                    rules={validationRules.currencyID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`currencyID-${index}`}
                                        className="block mb-1 font-medium"
                                      >
                                        Currency
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Select
                                        id={`currencyID-${index}`}
                                        isDisabled={
                                          isTravelSettlementWithLocation
                                        }
                                        options={
                                          isTravelSettlementWithLocation
                                            ? []
                                            : allCurrencies
                                        }
                                        value={
                                          isTravelSettlementWithLocation
                                            ? getCurrencyOptionByCode(
                                                getCurrencyCodeFromLocation(
                                                  expenseValues.location,
                                                ),
                                              )
                                            : allCurrencies.find(
                                                (c) =>
                                                  c.value === record.currencyID,
                                              ) || null
                                        }
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "currencyID",
                                            value,
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "currencyID"],
                                            value,
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Recharge */}
                                  <Form.Item
                                    name={["rows", index, "recharge"]}
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          "Please select recharge option",
                                      },
                                    ]}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`recharge-${index}`}
                                        className="block mb-1 font-medium"
                                      >
                                        Recharge
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>

                                      <Select
                                        id={`recharge-${index}`}
                                        className="h-45px rounded-md"
                                        options={rechargeOpts}
                                        value={rechargeOpts.find(
                                          (opt) =>
                                            opt.value ===
                                            form.getFieldValue([
                                              "rows",
                                              index,
                                              "recharge",
                                            ]),
                                        )}
                                        onChange={(selected) => {
                                          const value = selected?.value || null;
                                          form.setFieldValue(
                                            ["rows", index, "recharge"],
                                            value,
                                          );
                                          handleChange(
                                            index,
                                            "recharge",
                                            value,
                                          );
                                        }}
                                        getOptionLabel={(option) =>
                                          option.label
                                        }
                                        getOptionValue={(option) =>
                                          option.value
                                        }
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>

                                <Col xs={24} md={24} xl={24}>
                                  {/* Cost */}
                                  <Form.Item
                                    name={["rows", index, "cost"]}
                                    rules={validationRules.cost}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor="cost"
                                        className="block mb-1 font-medium"
                                      >
                                        Cost
                                        <span className="mandatory-symbol">
                                          *
                                        </span>
                                      </label>
                                      <Input
                                        placeholder="Enter cost"
                                        type="text"
                                        value={record.cost}
                                        className="form-control h-45px rounded-md"
                                        onChange={(e) =>
                                          handleChange(
                                            index,
                                            "cost",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  {/* Upload
            <Form.Item className="uploadNotification" label="Upload">
              <input
                id={`fileInput${index}`}
                type="file"
                accept=".png, .jpg, .jpeg, .pdf, .docx, .doc"
                onChange={(event) => handleFileChange(index, event)}
                style={{ display: "none" }}
              />
              <SubmitButton
                ky="upload"
                type="primary"
                submitName="Upload"
                className="btn mx-auto d-block defaultbutton rounded-lg text-white"
                onClick={() => handleButtonClick(index)}
              />
              {record.mediaInfo?.length ? (
                <Typography.Text>
                  {record.mediaInfo.length} file(s) attached
                </Typography.Text>
              ) : (
                <img
                  src={media.notification.svg}
                  alt="Upload info"
                  title="Allowed: jpg, png, pdf, docx (max 4MB)"
                />
              )}

      </Form.Item>
    */}
                                  <Form.Item name={["rows", index, "media"]}>
                                    <Dragger
                                      className="uploadDrag"
                                      customRequest={({ file, onSuccess }) =>
                                        handleFileChange(index, file, onSuccess)
                                      }
                                    >
                                      <p className="ant-upload-drag-icon">
                                        <img src={media.upload.svg} />
                                      </p>
                                      <p className="ant-upload-text">
                                        Drag your file(s) to start uploading
                                      </p>
                                      <p className="ant-upload-hint">OR</p>
                                      <span className="uploadBtn">
                                        <img
                                          src={media.uploadIcon.svg}
                                          alt="Upload info"
                                          title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                        />{" "}
                                        Browse file
                                      </span>
                                      <p className="ant-upload-text">
                                        Only one file allowed (.jpg, .png, .pdf,
                                        max 4MB)
                                      </p>
                                    </Dragger>
                                  </Form.Item>
                                </Col>

                                {/* Remove Row (optional) */}
                                {rows?.length > 1 && (
                                  <i
                                    className="flaticon-delete icon-lg cursor-pointer"
                                    onClick={() => handleRemoveRow(index)}
                                  ></i>
                                )}
                              </Row>
                            </div>
                          )}
                        />
                      </Table>
                    </div>

                    {/* <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <div className="my-5"></div>
                      </Col>
                    </Row> */}
                    <div className="text-center footerBtn">
                      <div className="footerReversment">
                        <small>Total Reimbursement</small>
                        <h4>
                          {process.env.REACT_APP_DEFAULT_CURRENCY}
                          {totalReimbursement}
                        </h4>
                      </div>
                      <div className="btnsWrap">
                        <SubmitButton
                          ky="saveAsDraft"
                          //type="primary"
                          htmlType="submit"
                          submitName="Save as draft"
                          className="defalutWhiteBtn"
                          onClick={() => {
                            setFormAction("draft");
                          }}
                          loading={false}
                        />

                        <SubmitButton
                          ky="submit"
                          //type="primary"
                          htmlType="submit"
                          submitName="Submit"
                          className="defaultbutton"
                          onClick={() => {
                            setFormAction("");
                          }}
                          loading={false}
                        />
                      </div>
                    </div>

                    <Modal
                      title={
                        formAction === "draft"
                          ? "Save as Draft"
                          : "Confirm Expense"
                      }
                      open={isModalVisible}
                      onCancel={handleCancel}
                      className="custom-modal"
                      footer={[
                        <SubmitButton
                          ky="back"
                          type="primary"
                          className="btn px-5 py-2 defaultWhiteBtn rounded-lg"
                          submitName="No"
                          onClick={handleCancel}
                        />,

                        <SubmitButton
                          ky="update"
                          type="primary"
                          htmlType="submit"
                          className="btn px-5 py-2 defaultbutton rounded-lg"
                          submitName="Yes"
                          onClick={handleOk}
                        />,
                      ]}
                    >
                      {formAction === "draft" ? (
                        <p>
                          Please review your entered information before saving
                          the form as a draft. You can make changes later before
                          final submission from <strong>List of Expense</strong>
                          .
                        </p>
                      ) : (
                        <p>
                          Please review your selected or entered information
                          carefully before submitting the form, as changes may
                          not be possible once it's submitted.
                        </p>
                      )}
                    </Modal>
                  </Form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
